"use client";

import { create } from "zustand";
import type { GalleryType } from "@/features/items/poiGalleries";
import type { MediaInfo } from "@/features/media/mediaInfo";
import { useModal } from "@/hooks/useModal";

/** The modal id, so the gallery buttons and the modal agree on which one. */
export const GALLERY_MODAL_ID = "gallery";

interface GalleryStore {
  items: MediaInfo[];
  type: GalleryType;
  /** Which slide is showing — the header reads its title from it. */
  index: number;
  setContents: (items: MediaInfo[], type: GalleryType) => void;
  setIndex: (index: number) => void;
}

/**
 * What the gallery is currently showing.
 *
 * Separate from `useModal`'s open/closed state because it is a different kind of
 * fact: which media the user asked for, not whether a dialog is up. Keeping the
 * contents here means the app-wide "one modal at a time" rule still governs
 * opening, and the gallery doesn't need its own.
 */
export const useGalleryStore = create<GalleryStore>((set) => ({
  items: [],
  type: "photo",
  index: 0,
  // Resetting the slide here rather than in the modal keeps it atomic with the
  // contents it belongs to — a second gallery opened from the same POI would
  // otherwise briefly show the previous gallery's slide number.
  setContents: (items, type) => set({ items, type, index: 0 }),
  setIndex: (index) => set({ index }),
}));

export function useGalleryModal() {
  const { isOpen, open, close } = useModal(GALLERY_MODAL_ID);
  const items = useGalleryStore((state) => state.items);
  const type = useGalleryStore((state) => state.type);
  const index = useGalleryStore((state) => state.index);
  const setIndex = useGalleryStore((state) => state.setIndex);
  const setContents = useGalleryStore((state) => state.setContents);

  return {
    isOpen,
    items,
    type,
    index,
    setIndex,
    open: (items: MediaInfo[], type: GalleryType) => {
      setContents(items, type);
      open();
    },
    close,
  };
}
